// Group Insurance — admin container: Submissions · Form Builder · Campaign · Audit.
// State office bearers get a read-only all-district submissions view;
// district office bearers get the same view locked to their own district.
import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import SubmissionsPanel from "@/components/insurance/SubmissionsPanel";
import FormBuilder from "@/components/insurance/FormBuilder";
import CampaignPanel from "@/components/insurance/CampaignPanel";
import AuditPanel from "@/components/insurance/AuditPanel";
import { isDistrictAdmin, isSuperAdmin } from "@/lib/roles";
import { HeartPulse } from "lucide-react";

export default function AdminInsuranceTab({ role = "admin", district = "" }) {
  const [tab, setTab] = useState("submissions");
  if (!isSuperAdmin(role)) {
    const locked = isDistrictAdmin(role) ? district : "";
    return (
      <div className="space-y-4" data-testid="admin-insurance">
        <Card className="p-4 flex items-start gap-3">
          <div className="w-11 h-11 rounded-lg bg-teal-600/10 text-teal-700 flex items-center justify-center shrink-0">
            <HeartPulse className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-lg">Group Health Insurance — Submissions</h3>
            <p className="text-sm text-muted-foreground">
              {locked
                ? `Member submissions from ${locked} district — view and export. Status changes are handled by the state office.`
                : "Member submissions across all districts — view and export. Status changes are handled by the super admin."}
            </p>
          </div>
        </Card>
        <SubmissionsPanel lockDistrict={locked} readOnly />
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="admin-insurance">
      <Card className="p-4 flex items-start gap-3">
        <div className="w-11 h-11 rounded-lg bg-teal-600/10 text-teal-700 flex items-center justify-center shrink-0">
          <HeartPulse className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-bold text-lg">Group Health Insurance — Management</h3>
          <p className="text-sm text-muted-foreground">Collected member & family data, the form builder, the home-page campaign section, and the audit trail — all without developer help.</p>
        </div>
      </Card>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="submissions" data-testid="gi-tab-submissions">Submissions</TabsTrigger>
          <TabsTrigger value="builder" data-testid="gi-tab-builder">Form Builder</TabsTrigger>
          <TabsTrigger value="campaign" data-testid="gi-tab-campaign">Campaign Section</TabsTrigger>
          <TabsTrigger value="audit" data-testid="gi-tab-audit">Audit Trail</TabsTrigger>
        </TabsList>
        <TabsContent value="submissions" className="mt-4"><SubmissionsPanel /></TabsContent>
        <TabsContent value="builder" className="mt-4"><FormBuilder /></TabsContent>
        <TabsContent value="campaign" className="mt-4"><CampaignPanel /></TabsContent>
        <TabsContent value="audit" className="mt-4"><AuditPanel /></TabsContent>
      </Tabs>
    </div>
  );
}
